import { Outlet } from 'react-router-dom';
import { ShieldCheckIcon } from 'lucide-react';
import UserDropdown from '@/components/ui/topBar/UserDropdown';
import { AdminSidebar } from './AdminSidebar';
import { GlobalSearch } from './components/GlobalSearch';
import { AdminCasePanel } from './features/AdminCasePanel';
import { useAdminCaseSelection } from './hooks/useAdminCaseSelection';
import { useAdminOverview } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

/**
 * A separate viewport from the clinic app: an admin works across clinics, so the
 * patient-centric shell (and its clinic-scoped data) is not the right frame.
 */
export function AdminLayout() {
  const { data: overview } = useAdminOverview();
  const { patientId, scanId, selectScan, closeCase } = useAdminCaseSelection();

  return (
    <div className="gradient-shell flex h-full w-full rounded-xl p-2">
      <div className="flex w-full flex-col gap-2">
        <header className="gradient-header relative z-[1000] shrink-0 rounded-xl text-white">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10">
                <ShieldCheckIcon className="h-[18px] w-[18px] text-primary-300" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-[9px] font-semibold uppercase tracking-[0.18em] text-white/35">
                  Zubee Factory
                </p>
                <h1 className="truncate text-sm font-semibold tracking-tight text-white/85">Admin Console</h1>
              </div>
            </div>

            <GlobalSearch className="order-last w-full min-w-0 md:order-none md:w-96" />

            <div className="flex shrink-0 items-center gap-2 border-l border-white/6 pl-4">
              <UserDropdown />
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        </header>

        <div className="flex min-h-0 flex-1 flex-col gap-2 md:flex-row">
          <AdminSidebar
            openIssueCount={overview?.totals.openIssueCount}
            pendingInviteCount={overview?.totals.pendingInvitationCount}
          />
          {/* The case panel sits beside the page rather than replacing it: an admin
              works down a list, and losing the list on every case would undo that. */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 xl:flex-row">
            <main
              className={cn(
                'min-h-0 min-w-0 flex-1 overflow-y-auto rounded-xl noise p-3',
                patientId && 'xl:w-1/2 xl:flex-none',
              )}
            >
              <Outlet />
            </main>

            {patientId && (
              <AdminCasePanel
                key={patientId}
                className="min-h-0 w-full flex-1 xl:w-1/2 xl:flex-none"
                patientId={patientId}
                scanId={scanId}
                onSelectScan={selectScan}
                onClose={closeCase}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLayout;
